/**
 * Pre-Questions Card Module
 * Displays a waiting card before users complete their first listen
 */

import { EVENT_TYPES } from '../../core/event-types.js';
import { logEvent } from '../../core/event-logger.js';
import appState from '../../core/app-state.js';
import { showToast } from '../../ui/ui-helpers.js';
import type { Clip } from '../../types/clip.js';

/**
 * Callback type for rendering the first question
 */
type RenderFirstQuestionCallback = (clip: Clip, idx: number) => void;

/**
 * Registered callback to render the first question
 */
let renderFirstQuestionCallback: RenderFirstQuestionCallback | null = null;

/**
 * Store audio event listeners for cleanup
 */
interface AudioListeners {
  audio: HTMLAudioElement;
  playHandler: () => void;
  timeUpdateHandler: () => void;
  pauseHandler: () => void;
}

let activeListeners: AudioListeners | null = null;

/**
 * Clean up audio event listeners
 */
function cleanupAudioListeners(): void {
  if (activeListeners) {
    const { audio, playHandler, timeUpdateHandler, pauseHandler } = activeListeners;
    audio.removeEventListener('play', playHandler);
    audio.removeEventListener('timeupdate', timeUpdateHandler);
    audio.removeEventListener('pause', pauseHandler);
    activeListeners = null;
  }
}

/**
 * Register the callback to render the first question
 * @param callback - The render function to call when revealing questions
 */
export function registerRenderFirstQuestion(callback: RenderFirstQuestionCallback): void {
  renderFirstQuestionCallback = callback;
}

/**
 * Render the pre-questions card with listen prompt
 * @param container - The container element to render into
 */
export function renderPreQuestionsCard(container: HTMLElement): void {
  const card = document.createElement('div');
  card.className = 'pre-questions-card';
  card.setAttribute('role', 'group');
  card.setAttribute('aria-label', 'Listen to audio before answering questions');
  
  const content = document.createElement('div');
  content.className = 'pre-questions-content';
  
  // Create headphones SVG icon
  const icon = document.createElement('div');
  icon.className = 'listen-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
      <path d="M12 3C7.58172 3 4 6.58172 4 11V17C4 18.1046 4.89543 19 6 19H7C7.55228 19 8 18.5523 8 18V13C8 12.4477 7.55228 12 7 12H6V11C6 7.68629 8.68629 5 12 5C15.3137 5 18 7.68629 18 11V12H17C16.4477 12 16 12.4477 16 13V18C16 18.5523 16.4477 19 17 19H18C19.1046 19 20 18.1046 20 17V11C20 6.58172 16.4183 3 12 3Z" fill="currentColor"/>
    </svg>
  `;
  content.appendChild(icon);
  
  // Create primary text
  const text = document.createElement('p');
  text.className = 'pre-questions-text';
  text.textContent = 'Listen first — questions next';
  content.appendChild(text);
  
  // Create actions container
  const actions = document.createElement('div');
  actions.className = 'pre-questions-actions';
  
  // Create "Start questions now" button (only shown after audio starts and 20% progress)
  const startBtn = document.createElement('button');
  startBtn.className = 'btn-text start-now';
  startBtn.type = 'button';
  startBtn.textContent = 'Start questions now';
  startBtn.setAttribute('aria-label', 'Skip to questions now');
  startBtn.hidden = true; // Hidden initially
  
  startBtn.addEventListener('click', () => {
    logEvent(EVENT_TYPES['QUESTIONS_EARLY_START']!, { 
      clipId: appState.activeClipId 
    });
    revealQuestions();
  });
  
  actions.appendChild(startBtn);
  content.appendChild(actions);
  card.appendChild(content);
  container.appendChild(card);
  
  // Show the start button after audio starts playing and reaches 20% progress
  const audio = appState.refs.audioEl;
  if (audio) {
    let hasRevealed = false;
    
    const updateActionVisibility = () => {
      if (!audio || hasRevealed) return;
      const progress = audio.duration ? (audio.currentTime / audio.duration) : 0;
      // Show button only after 20% progress while playing
      const shouldShow = !audio.paused && progress >= 0.2;
      
      if (shouldShow && startBtn.hidden) {
        startBtn.hidden = false;
        // Add fade-in class for smooth appearance
        requestAnimationFrame(() => {
          startBtn.classList.add('fade-in');
        });
        hasRevealed = true;
        // Clean up all listeners once revealed
        cleanupAudioListeners();
      }
    };
    
    const pauseHandler = () => {
      if (!hasRevealed) {
        startBtn.hidden = true;
      }
    };
    
    audio.addEventListener('play', updateActionVisibility);
    audio.addEventListener('timeupdate', updateActionVisibility);
    audio.addEventListener('pause', pauseHandler);
    
    // Store listeners for cleanup
    activeListeners = {
      audio,
      playHandler: updateActionVisibility,
      timeUpdateHandler: updateActionVisibility,
      pauseHandler
    };
    
    // If audio is already playing, check progress immediately
    if (!audio.paused) {
      updateActionVisibility();
    }
  }
}

/**
 * Reveal questions by removing the pre-questions card and showing first question
 */
export function revealQuestions(): void {
  const card = document.querySelector('.pre-questions-card');
  if (!card) return;
  
  // Clean up any active audio event listeners
  cleanupAudioListeners();
  
  // Mark first listen as complete
  if (appState.activeClipId) {
    appState.firstListenComplete[appState.activeClipId] = true;
  }
  
  // Add fade-out class
  card.classList.add('fade-out');
  
  // Wait for fade animation, then remove card and show questions
  setTimeout(() => {
    card.remove();
    
    // Show toast notification
    showToast("Nice! You're warmed up — let's go.", 'success', 2000);
    
    // Log auto-reveal event
    logEvent(EVENT_TYPES['QUESTIONS_AUTO_REVEAL']!, { 
      clipId: appState.activeClipId 
    });
    
    // Render the first question
    if (renderFirstQuestionCallback && appState.currentClip) {
      renderFirstQuestionCallback(appState.currentClip, appState.currentClipIdx);
    }
  }, 200);
}

/**
 * Check if the pre-questions card should be shown for a clip
 * @param clip - The clip to check
 * @returns true if the card should be shown
 */
export function shouldShowPreQuestionsCard(clip: Clip): boolean {
  // Don't show if no questions
  if (!clip.questions || clip.questions.length === 0) {
    return false;
  }
  
  // Don't show if first listen already completed
  if (appState.firstListenComplete[clip.id]) {
    return false;
  }
  
  return true;
}
